/**
 * Persistencia local de notas de voz por Study Instance UID (IndexedDB / Dexie).
 */
import Dexie from 'dexie'

const DB_NAME = 'udvVoiceNotes'

const db = new Dexie(DB_NAME)

db.version(1).stores({
  /** PK id; índice por estudio para listar al reabrir. */
  notes: 'id, studyInstanceUID, createdAt',
})

db.version(2).stores({
  notes: 'id, studyInstanceUID, createdAt',
}).upgrade((tx) =>
  tx
    .table('notes')
    .toCollection()
    .modify((row) => {
      if (row.transcriptStatus == null) row.transcriptStatus = 'skipped'
      if (row.transcript == null) row.transcript = null
    })
)

/**
 * @typedef {'pending'|'ready'|'error'|'skipped'} TranscriptStatus
 *
 * @typedef {{
 *   id: string,
 *   studyInstanceUID: string,
 *   name: string,
 *   mp3Blob: Blob,
 *   durationMs: number,
 *   size: number,
 *   createdAt: number,
 *   alignment?: object|null,
 *   patientName?: string,
 *   patientId?: string,
 *   studyDate?: string,
 *   studyDescription?: string,
 *   modality?: string,
 *   transcript?: { text: string, source?: string, words?: object[] }|null,
 *   transcriptStatus?: TranscriptStatus,
 * }} VoiceNoteRow
 */

export function openVoiceNotesDb() {
  return db.open()
}

/**
 * @param {VoiceNoteRow} row
 */
export async function saveVoiceNote(row) {
  if (!row || !row.id || !row.studyInstanceUID || !row.mp3Blob) {
    throw new Error('Nota de voz incompleta para persistir')
  }
  await db.open()
  await db.notes.put({
    id: String(row.id),
    studyInstanceUID: String(row.studyInstanceUID),
    name: row.name || 'nota_medica.mp3',
    mp3Blob: row.mp3Blob,
    durationMs: Number(row.durationMs) || 0,
    size: Number(row.size) || (row.mp3Blob && row.mp3Blob.size) || 0,
    createdAt: Number(row.createdAt) || Date.now(),
    alignment: row.alignment || null,
    patientName: row.patientName || '',
    patientId: row.patientId || '',
    studyDate: row.studyDate || '',
    studyDescription: row.studyDescription || '',
    modality: row.modality || '',
    transcript: row.transcript || null,
    transcriptStatus: row.transcriptStatus || 'skipped',
  })
}

/**
 * Actualiza solo campos de transcripción (sin tocar el blob).
 * @param {string} id
 * @param {{
 *   transcript?: { text: string, source?: string }|null,
 *   transcriptStatus?: TranscriptStatus,
 * }} patch
 */
export async function updateVoiceNoteTranscript(id, patch) {
  if (!id || !patch) return
  await db.open()
  const key = String(id)
  const existing = await db.notes.get(key)
  if (!existing) return
  const next = { ...existing }
  if (Object.prototype.hasOwnProperty.call(patch, 'transcript')) {
    next.transcript = patch.transcript || null
  }
  if (patch.transcriptStatus) {
    next.transcriptStatus = patch.transcriptStatus
  }
  await db.notes.put(next)
}

/**
 * @param {string} studyInstanceUID
 * @returns {Promise<VoiceNoteRow[]>}
 */
export async function listVoiceNotesForStudy(studyInstanceUID) {
  const uid = studyInstanceUID != null ? String(studyInstanceUID).trim() : ''
  if (!uid) return []
  await db.open()
  const rows = await db.notes.where('studyInstanceUID').equals(uid).toArray()
  rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  return rows
}

/**
 * @param {string} id
 */
export async function deleteVoiceNote(id) {
  if (!id) return
  await db.open()
  await db.notes.delete(String(id))
}

/**
 * @param {VoiceNoteRow} row
 * @returns {object} entrada UI con objectUrl
 */
export function voiceNoteRowToUi(row) {
  if (!row || !row.mp3Blob) return null
  let objectUrl = ''
  try {
    objectUrl = URL.createObjectURL(row.mp3Blob)
  } catch (e) {
    return null
  }
  return {
    id: row.id,
    name: row.name,
    mp3Blob: row.mp3Blob,
    objectUrl,
    durationMs: row.durationMs,
    size: row.size,
    createdAt: row.createdAt,
    alignment: row.alignment || null,
    studyInstanceUID: row.studyInstanceUID,
    patientName: row.patientName || '',
    patientId: row.patientId || '',
    studyDate: row.studyDate || '',
    studyDescription: row.studyDescription || '',
    modality: row.modality || '',
    transcript: row.transcript || null,
    transcriptStatus: row.transcriptStatus || 'skipped',
  }
}

/**
 * @param {Array<{ objectUrl?: string }>} notes
 */
export function revokeVoiceNoteObjectUrls(notes) {
  if (!Array.isArray(notes)) return
  for (let i = 0; i < notes.length; i++) {
    const n = notes[i]
    if (n && n.objectUrl) {
      try {
        URL.revokeObjectURL(n.objectUrl)
      } catch (e) {
        /* ignore */
      }
    }
  }
}

export { db as voiceNotesDb }
export default db
